import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Hint } from './Hint';
import { ChevronLeftIcon, PlusIcon, PrinterIcon, DownloadIcon } from './Icons';

const REPORT_ZOOM_STEPS = [0.5, 0.67, 0.75, 0.85, 1, 1.15, 1.25, 1.5, 1.75, 2] as const;
const REPORT_ZOOM_DEFAULT = 1;
const REPORT_ZOOM_MIN = REPORT_ZOOM_STEPS[0];
const REPORT_ZOOM_MAX = REPORT_ZOOM_STEPS[REPORT_ZOOM_STEPS.length - 1];

const MinusIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const FitWidthIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 12h16M7 8l-3 4 3 4M17 8l3 4-3 4"
    />
  </svg>
);

function clampZoom(value: number): number {
  return Math.min(REPORT_ZOOM_MAX, Math.max(REPORT_ZOOM_MIN, value));
}

export type ReportPageSize = 'A4 portrait' | 'A4 landscape';

export type ReportViewProps = {
  backTo: string;
  backLabel?: string;
  title?: string;
  subtitle?: React.ReactNode;
  /** Extra controls left of zoom / Print (print:hidden). */
  actions?: React.ReactNode;
  empty?: boolean;
  emptyMessage?: React.ReactNode;
  printDisabled?: boolean;
  /** Optional Excel export (shows Excel button left of Print). */
  onExportExcel?: () => void | Promise<void>;
  printPageSize?: ReportPageSize;
  printMargin?: string;
  /** Show on-screen zoom controls. Default true. */
  showZoom?: boolean;
  className?: string;
  children: React.ReactNode;
};

/**
 * On-screen chrome for printable HTML reports (toolbar, zoom, Print / Save as PDF).
 * Pair with {@link ReportPack} / {@link ReportPage} for the printable body.
 *
 * Zoom shortcuts (when not typing in an input): `+` / `=` zoom in, `-` zoom out, `0` reset, `f` fit width.
 */
export const ReportView: React.FC<ReportViewProps> = ({
  backTo,
  backLabel = 'Back',
  title,
  subtitle,
  actions,
  empty = false,
  emptyMessage = 'Nothing to show for this report.',
  printDisabled = false,
  onExportExcel,
  printPageSize = 'A4 portrait',
  printMargin = '5mm',
  showZoom = true,
  className,
  children,
}) => {
  const { colors } = useTheme();
  const landscape = printPageSize.includes('landscape');
  const paperWidthMm = landscape ? 297 : 210;
  const [zoom, setZoom] = useState(REPORT_ZOOM_DEFAULT);
  const [excelLoading, setExcelLoading] = useState(false);
  const hostWrapRef = useRef<HTMLDivElement>(null);

  const handleExportExcel = useCallback(async () => {
    if (!onExportExcel || empty || excelLoading) return;
    setExcelLoading(true);
    try {
      await onExportExcel();
    } finally {
      setExcelLoading(false);
    }
  }, [onExportExcel, empty, excelLoading]);

  const canZoomOut = zoom > REPORT_ZOOM_MIN + 0.001;
  const canZoomIn = zoom < REPORT_ZOOM_MAX - 0.001;
  const zoomLabel = `${Math.round(zoom * 100)}%`;

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      for (let i = REPORT_ZOOM_STEPS.length - 1; i >= 0; i -= 1) {
        if (REPORT_ZOOM_STEPS[i] < z - 0.001) return REPORT_ZOOM_STEPS[i];
      }
      return REPORT_ZOOM_MIN;
    });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => {
      for (let i = 0; i < REPORT_ZOOM_STEPS.length; i += 1) {
        if (REPORT_ZOOM_STEPS[i] > z + 0.001) return REPORT_ZOOM_STEPS[i];
      }
      return REPORT_ZOOM_MAX;
    });
  }, []);

  const zoomReset = useCallback(() => setZoom(REPORT_ZOOM_DEFAULT), []);

  const fitToWidth = useCallback(() => {
    const wrap = hostWrapRef.current;
    if (!wrap) return;
    const available = wrap.clientWidth;
    if (available <= 0) return;
    const paperPx = paperWidthMm * (96 / 25.4);
    const next = clampZoom(available / paperPx);
    setZoom(Math.round(next * 100) / 100);
  }, [paperWidthMm]);

  useEffect(() => {
    if (!showZoom || empty) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        zoomReset();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        fitToWidth();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showZoom, empty, zoomIn, zoomOut, zoomReset, fitToWidth]);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col print:block print:overflow-visible',
        colors.background,
        className,
      )}
      tabIndex={-1}
    >
      <style>{`
        @media print {
          @page {
            size: ${printPageSize};
            margin-top: ${printMargin};
            margin-right: ${printMargin};
            margin-left: ${printMargin};
            margin-bottom: 14mm;
            @bottom-center {
              content: "Page " counter(page) " of " counter(pages);
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 9pt;
              color: #475569;
              vertical-align: middle;
            }
          }
        }
      `}</style>

      <header
        className={cn(
          'sticky top-0 z-20 shrink-0 border-b print:hidden',
          colors.border,
          colors.surface,
          'supports-[backdrop-filter]:bg-opacity-90 supports-[backdrop-filter]:backdrop-blur-md',
        )}
      >
        <div className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Hint content={backLabel} side="bottom" delay={180} triggerClassName="inline-flex shrink-0">
              <Link
                to={backTo}
                aria-label={backLabel}
                className={cn(
                  'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                  colors.border,
                  colors.textSecondary,
                  colors.interactiveHover,
                )}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Link>
            </Hint>
            {(title || subtitle) && (
              <div className="min-w-0">
                {title ? (
                  <h1 className={cn('truncate text-sm font-semibold tracking-tight', colors.text)}>
                    {title}
                  </h1>
                ) : null}
                {subtitle ? (
                  <p className={cn('truncate text-[11px] leading-tight', colors.textMuted)}>{subtitle}</p>
                ) : null}
              </div>
            )}
          </div>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}

            {showZoom && !empty ? (
              <div
                className={cn(
                  'inline-flex items-center rounded-lg border p-0.5',
                  colors.border,
                  colors.surfaceHover,
                )}
                role="group"
                aria-label="Report zoom"
              >
                <Hint content="Zoom out (−)" side="bottom" delay={180} triggerClassName="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 px-0"
                    disabled={!canZoomOut}
                    onClick={zoomOut}
                    aria-label="Zoom out"
                  >
                    <MinusIcon className="h-3.5 w-3.5" />
                  </Button>
                </Hint>
                <Hint content="Reset zoom to 100% (0)" side="bottom" delay={180} triggerClassName="inline-flex">
                  <button
                    type="button"
                    onClick={zoomReset}
                    className={cn(
                      'min-w-[2.75rem] rounded-md px-1 py-1 text-center text-[11px] font-semibold tabular-nums',
                      colors.text,
                      colors.interactiveHover,
                    )}
                    aria-label={`Zoom ${zoomLabel}. Click to reset.`}
                  >
                    {zoomLabel}
                  </button>
                </Hint>
                <Hint content="Zoom in (+)" side="bottom" delay={180} triggerClassName="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 px-0"
                    disabled={!canZoomIn}
                    onClick={zoomIn}
                    aria-label="Zoom in"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                  </Button>
                </Hint>
                <div className={cn('mx-0.5 h-4 w-px', colors.border)} aria-hidden />
                <Hint content="Fit to width (F)" side="bottom" delay={180} triggerClassName="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 px-0"
                    onClick={fitToWidth}
                    aria-label="Fit to width"
                  >
                    <FitWidthIcon className="h-3.5 w-3.5" />
                  </Button>
                </Hint>
              </div>
            ) : null}

            {onExportExcel ? (
              <Hint content="Download Excel (.xlsx)" side="bottom" delay={180} triggerClassName="inline-flex">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8"
                  disabled={printDisabled || empty || excelLoading}
                  onClick={() => void handleExportExcel()}
                  aria-label="Export to Excel"
                >
                  <DownloadIcon className="h-4 w-4" />
                  {excelLoading ? 'Excel…' : 'Excel'}
                </Button>
              </Hint>
            ) : null}

            <Hint
              content="Browser print dialog → Save as PDF"
              side="bottom"
              delay={180}
              triggerClassName="inline-flex"
            >
              <Button
                type="button"
                variant="accent"
                size="sm"
                className="h-8"
                disabled={printDisabled || empty}
                onClick={() => window.print()}
                aria-label="Print / Save as PDF"
              >
                <PrinterIcon className="h-4 w-4" />
                Print
              </Button>
            </Hint>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto print:overflow-visible">
        {empty ? (
          <p
            className={cn(
              'mx-auto mt-8 max-w-lg rounded-xl border px-4 py-8 text-center text-sm print:hidden',
              colors.border,
              colors.surface,
              colors.textMuted,
            )}
          >
            {emptyMessage}
          </p>
        ) : (
          <div ref={hostWrapRef} className="px-4 py-4 print:p-0">
            <div
              className="ui-report-zoom-host mx-auto print:mx-0 print:w-auto"
              style={{
                width: `${paperWidthMm}mm`,
                zoom,
              }}
            >
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export type ReportPackProps = {
  className?: string;
  children: React.ReactNode;
};

export type ReportSheetFooterProps = {
  pageNumber: number;
  pageCount: number;
  printedAt?: string;
  className?: string;
};

/** Shared sheet footer: Date/Time left, Page X of N right. */
export const ReportSheetFooter: React.FC<ReportSheetFooterProps> = ({
  pageNumber,
  pageCount,
  printedAt,
  className,
}) => {
  const when = printedAt || formatDateTime(new Date());
  return (
    <div
      className={cn('ui-report-page-footer', className)}
      aria-label={`Date/Time: ${when}. Page ${pageNumber} of ${pageCount}`}
    >
      <span className="ui-report-page-footer-left">Date/Time: {when}</span>
      <span className="ui-report-page-footer-right">
        Page {pageNumber} of {pageCount}
      </span>
    </div>
  );
};

function isReportPageChild(
  child: React.ReactNode,
): child is React.ReactElement<ReportPageProps> {
  if (!React.isValidElement(child)) return false;
  const t = child.type as { displayName?: string } | string;
  if (t === ReportPage) return true;
  return typeof t === 'function' && (t as { displayName?: string }).displayName === 'ReportPage';
}

/** Print root — stamps Date/Time + Page X of N on each {@link ReportPage}. */
export const ReportPack: React.FC<ReportPackProps> = ({ className, children }) => {
  const printedAt = useMemo(() => formatDateTime(new Date()), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<Comment | null>(null);
  const childArray = React.Children.toArray(children);
  const pageCount = childArray.filter(isReportPageChild).length;
  let pageIndex = 0;

  /**
   * Chrome clips multi-page print when the pack sits under layout `overflow:hidden`
   * / `h-screen` ancestors. Park the pack on `document.body` for the print job only.
   */
  useEffect(() => {
    const onBeforePrint = () => {
      const el = rootRef.current;
      if (!el || el.parentElement === document.body) return;
      document.body.classList.add('printing-ui-report-pack');
      const marker = document.createComment('ui-report-pack-placeholder');
      placeholderRef.current = marker;
      el.parentNode?.insertBefore(marker, el);
      document.body.appendChild(el);
    };
    const onAfterPrint = () => {
      const el = rootRef.current;
      const marker = placeholderRef.current;
      if (el && marker?.parentNode) {
        marker.parentNode.insertBefore(el, marker);
        marker.parentNode.removeChild(marker);
      }
      placeholderRef.current = null;
      document.body.classList.remove('printing-ui-report-pack');
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      onAfterPrint();
    };
  }, []);

  return (
    <div ref={rootRef} className={cn('app-print-root ui-report-pack', className)}>
      {childArray.map((child, key) => {
        if (!isReportPageChild(child)) {
          return <React.Fragment key={key}>{child}</React.Fragment>;
        }
        pageIndex += 1;
        const pageNumber = pageIndex;
        const isLast = pageNumber >= Math.max(pageCount, 1);
        return (
          <ReportPage
            key={child.key ?? key}
            breakAfter={child.props.breakAfter ?? (!isLast && pageCount > 1)}
            size={child.props.size}
            className={child.props.className}
            pageNumber={child.props.pageNumber ?? pageNumber}
            pageCount={child.props.pageCount ?? Math.max(pageCount, 1)}
            printedAt={child.props.printedAt ?? printedAt}
            showFooter={child.props.showFooter}
          >
            {child.props.children}
          </ReportPage>
        );
      })}
    </div>
  );
};

export type ReportPageProps = {
  /** Force page break after this sheet when printing a pack. */
  breakAfter?: boolean;
  size?: 'portrait' | 'landscape';
  className?: string;
  pageNumber?: number;
  pageCount?: number;
  printedAt?: string;
  /** Default true — Date/Time left, Page X of N right. */
  showFooter?: boolean;
  children: React.ReactNode;
};

/** One printable sheet (A4 portrait or landscape preview). */
export const ReportPage: React.FC<ReportPageProps> = ({
  breakAfter = false,
  size = 'portrait',
  className,
  pageNumber = 1,
  pageCount = 1,
  printedAt,
  showFooter = true,
  children,
}) => (
  <article
    className={cn(
      'ui-report-page',
      size === 'landscape' ? 'ui-report-page-landscape' : 'ui-report-page-portrait',
      breakAfter && 'ui-report-page-break',
      className,
    )}
  >
    <div className="ui-report-page-body">{children}</div>
    {showFooter ? (
      <ReportSheetFooter
        pageNumber={pageNumber}
        pageCount={pageCount}
        printedAt={printedAt}
      />
    ) : null}
  </article>
);
ReportPage.displayName = 'ReportPage';

const MM_TO_PX = 96 / 25.4;
const FLOW_PAGE_HEIGHT_MM = { portrait: 297, landscape: 210 } as const;
const FLOW_PAGE_WIDTH_MM = { portrait: 210, landscape: 297 } as const;
/** Inner padding of `.ui-report-page`. */
const FLOW_PAGE_PAD_MM = 5;
/** Reserved strip for “Page X of N”. */
const FLOW_FOOTER_MM = 7;
/** Allow small measurement error before pushing lastFooter to a new sheet. */
const FLOW_FOOTER_FIT_SLACK_MM = 10;

export type ReportFlowPagesProps = {
  size?: 'portrait' | 'landscape';
  className?: string;
  /** Full header on the first sheet only. */
  firstHeader: React.ReactNode;
  /** Compact header on continuation sheets. */
  continueHeader?: React.ReactNode;
  /** Closing block on the last sheet only (signatures, notes). */
  lastFooter?: React.ReactNode;
  /** Self-contained blocks packed top-to-bottom until each sheet is full. */
  items: React.ReactNode[];
  itemKeys?: Array<string | number>;
};

/**
 * Packs report blocks onto full A4 sheets and stamps Page X of N in the sheet footer.
 * Content fills each page; the page number stays pinned to the bottom of the sheet.
 */
export const ReportFlowPages: React.FC<ReportFlowPagesProps> = ({
  size = 'portrait',
  className,
  firstHeader,
  continueHeader,
  lastFooter,
  items,
  itemKeys,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [pageIndexGroups, setPageIndexGroups] = useState<number[][]>(() =>
    items.length ? [items.map((_, i) => i)] : [[]],
  );

  const pack = useCallback(() => {
    const root = measureRef.current;
    if (!root) return;

    const pageH =
      FLOW_PAGE_HEIGHT_MM[size] * MM_TO_PX -
      2 * FLOW_PAGE_PAD_MM * MM_TO_PX -
      FLOW_FOOTER_MM * MM_TO_PX;

    const firstHeaderH =
      root.querySelector<HTMLElement>('[data-flow-first-header]')?.getBoundingClientRect()
        .height ?? 0;
    const continueHeaderH =
      root.querySelector<HTMLElement>('[data-flow-continue-header]')?.getBoundingClientRect()
        .height ?? 0;
    const lastFooterH =
      root.querySelector<HTMLElement>('[data-flow-last-footer]')?.getBoundingClientRect()
        .height ?? 0;
    const heights = Array.from(root.querySelectorAll<HTMLElement>('[data-flow-item]')).map(
      (el) => el.getBoundingClientRect().height,
    );

    if (!heights.length) {
      setPageIndexGroups([[]]);
      return;
    }

    const headerFor = (pageIdx: number) => (pageIdx === 0 ? firstHeaderH : continueHeaderH);
    const groups: number[][] = [];
    let bucket: number[] = [];
    let used = 0;

    const flush = () => {
      if (bucket.length) groups.push(bucket);
      bucket = [];
      used = 0;
    };

    const bucketUsed = (pageIdx: number, indices: number[]) =>
      headerFor(pageIdx) + indices.reduce((sum, idx) => sum + heights[idx], 0);

    for (let i = 0; i < heights.length; i += 1) {
      const h = heights[i];
      if (bucket.length === 0) {
        bucket = [i];
        used = headerFor(groups.length) + h;
      } else if (used + h <= pageH) {
        bucket.push(i);
        used += h;
      } else {
        flush();
        bucket = [i];
        used = headerFor(groups.length) + h;
      }
    }
    flush();

    if (!groups.length) groups.push([]);

    if (lastFooterH > 0) {
      const lastIdx = groups.length - 1;
      const lastUsed = bucketUsed(lastIdx, groups[lastIdx]);
      const slackPx = FLOW_FOOTER_FIT_SLACK_MM * MM_TO_PX;
      if (lastUsed + lastFooterH > pageH + slackPx) {
        groups.push([]);
      }
    }

    setPageIndexGroups(groups);
  }, [size, items.length, firstHeader, continueHeader, lastFooter]);

  useLayoutEffect(() => {
    pack();
  }, [pack, items]);

  useEffect(() => {
    const root = measureRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => pack());
    ro.observe(root);
    return () => ro.disconnect();
  }, [pack]);

  const total = Math.max(pageIndexGroups.length, 1);
  const widthMm = FLOW_PAGE_WIDTH_MM[size];

  const measureNode = (
    <div className="ui-report-flow-measure-host" aria-hidden>
      <div
        ref={measureRef}
        className="ui-report-flow-measure"
        style={{ width: `${widthMm - 2 * FLOW_PAGE_PAD_MM}mm` }}
      >
        <div data-flow-first-header>{firstHeader}</div>
        <div data-flow-continue-header>{continueHeader ?? null}</div>
        <div data-flow-last-footer>{lastFooter ?? null}</div>
        {items.map((item, i) => (
          <div key={itemKeys?.[i] ?? i} data-flow-item>
            {item}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {typeof document !== 'undefined' ? createPortal(measureNode, document.body) : null}

      <ReportPack className={className}>
        {pageIndexGroups.map((indices, pageIdx) => {
          const isFirst = pageIdx === 0;
          const isLast = pageIdx === total - 1;
          return (
            <ReportPage
              key={pageIdx}
              size={size}
              breakAfter={!isLast}
              className="ui-report-page-filled"
              pageNumber={pageIdx + 1}
              pageCount={total}
            >
              {isFirst ? firstHeader : indices.length > 0 ? continueHeader : null}
              {indices.map((itemIndex) => (
                <React.Fragment key={itemKeys?.[itemIndex] ?? itemIndex}>
                  {items[itemIndex]}
                </React.Fragment>
              ))}
              {isLast ? lastFooter : null}
            </ReportPage>
          );
        })}
      </ReportPack>
    </>
  );
};

export type ReportHeaderProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({ className, children }) => (
  <header className={cn('ui-report-header', className)}>{children}</header>
);

export type ReportTitleRowProps = {
  title: React.ReactNode;
  formId?: React.ReactNode;
  /** Optional brand / management-company logo (blob or data URL). */
  logoSrc?: string | null;
  logoAlt?: string;
  className?: string;
};

export const ReportTitleRow: React.FC<ReportTitleRowProps> = ({
  title,
  formId,
  logoSrc,
  logoAlt = 'Company logo',
  className,
}) => (
  <div className={cn('ui-report-title-row', className)}>
    <div className="ui-report-title-side ui-report-title-side-start">
      {logoSrc ? <img src={logoSrc} alt={logoAlt} className="ui-report-logo" /> : null}
    </div>
    <h1 className="ui-report-title">{title}</h1>
    <div className="ui-report-title-side ui-report-title-side-end">
      {formId != null && formId !== false ? (
        <span className="ui-report-form-id">{formId}</span>
      ) : null}
    </div>
  </div>
);

export type ReportMetaProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportMeta: React.FC<ReportMetaProps> = ({ className, children }) => (
  <table className={cn('ui-report-meta', className)}>
    <tbody>{children}</tbody>
  </table>
);

export type ReportMetaRowProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportMetaRow: React.FC<ReportMetaRowProps> = ({ className, children }) => (
  <tr className={className}>{children}</tr>
);

export type ReportFieldProps = {
  label: React.ReactNode;
  value?: React.ReactNode;
  colSpan?: number;
  className?: string;
  children?: React.ReactNode;
};

/** Label + value cell for report meta grids. */
export const ReportField: React.FC<ReportFieldProps> = ({
  label,
  value,
  colSpan,
  className,
  children,
}) => (
  <td colSpan={colSpan} className={className}>
    <span className="ui-report-label">{label}</span>
    <span className="ui-report-value">{children ?? value ?? '—'}</span>
  </td>
);

export type ReportLegalProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportLegal: React.FC<ReportLegalProps> = ({ className, children }) => (
  <p className={cn('ui-report-legal', className)}>{children}</p>
);

export type ReportSectionProps = {
  title?: React.ReactNode;
  /** Start on a new printed page. */
  breakBefore?: boolean;
  className?: string;
  children: React.ReactNode;
};

/** Titled body block used inside a report page (tables, lists, notes). */
export const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  breakBefore = false,
  className,
  children,
}) => (
  <section
    className={cn('ui-report-section', breakBefore && 'ui-report-break-before', className)}
  >
    {title != null && title !== false ? <h2 className="ui-report-section-title">{title}</h2> : null}
    {children}
  </section>
);

export type ReportBodyProps = {
  className?: string;
  children: React.ReactNode;
};

/** Flexible middle area between header and footer. */
export const ReportBody: React.FC<ReportBodyProps> = ({ className, children }) => (
  <div className={cn('ui-report-body', className)}>{children}</div>
);

export type ReportFooterProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportFooter: React.FC<ReportFooterProps> = ({ className, children }) => (
  <footer className={cn('ui-report-footer', className)}>{children}</footer>
);

export type ReportAgreeProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportAgree: React.FC<ReportAgreeProps> = ({ className, children }) => (
  <p className={cn('ui-report-agree', className)}>{children}</p>
);

export type ReportSignsProps = {
  columns?: 2 | 3 | 4;
  className?: string;
  children: React.ReactNode;
};

export const ReportSigns: React.FC<ReportSignsProps> = ({
  columns = 3,
  className,
  children,
}) => (
  <div
    className={cn(
      'ui-report-signs',
      columns === 2 && 'ui-report-signs-2',
      columns === 4 && 'ui-report-signs-4',
      className,
    )}
  >
    {children}
  </div>
);

export type ReportSignLineProps = {
  label: React.ReactNode;
  className?: string;
};

export const ReportSignLine: React.FC<ReportSignLineProps> = ({ label, className }) => (
  <div className={cn('ui-report-sign', className)}>
    <span className="ui-report-label">{label}</span>
    <span className="ui-report-sign-line" aria-hidden />
  </div>
);

export type ReportNoteProps = {
  className?: string;
  children: React.ReactNode;
};

export const ReportNote: React.FC<ReportNoteProps> = ({ className, children }) => (
  <p className={cn('ui-report-note', className)}>{children}</p>
);

export type ReportCheckProps = {
  label: string;
  checked?: boolean;
  className?: string;
};

/** Checkbox mark used in report meta (yes/no). */
export const ReportCheck: React.FC<ReportCheckProps> = ({ label, checked = false, className }) => (
  <span className={cn('ui-report-check', checked && 'on', className)}>
    {label} {checked ? '☑' : '☐'}
  </span>
);
